import { useLanguage } from '../contexts/LanguageContext';
import { useEffect, useRef, useState } from 'react';
import DisplayCards from './DisplayCards';
import { Box } from '@mui/material';
import { 
    BusinessCenter, 
    CheckCircle,
    Star,
    FlashOn,
    Diamond,
    Psychology,
    ArrowForward,
    ArrowBack
} from '@mui/icons-material';

export default function WhoSection() {
    const { t, isRTL } = useLanguage();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLParagraphElement>(null);
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const statsRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        // Animate title
                        if (entry.target === titleRef.current) {
                            entry.target.classList.add('animate-fade-in-up');
                        }
                        if (entry.target === descriptionRef.current) {
                            entry.target.classList.add('animate-fade-in-up');
                        }
                        if (entry.target === statsRef.current) {
                            entry.target.classList.add('animate-fade-in-up');
                        }
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe all elements
        if (titleRef.current) observer.observe(titleRef.current);
        if (descriptionRef.current) observer.observe(descriptionRef.current);
        if (statsRef.current) observer.observe(statsRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-[6rem]  h-auto min-h-[90vh] relative overflow-hidden">
            
            
            {/* Enhanced Corner Decorations with Animation */}
            <div className="absolute top-[22px] -left-[15px] sm:-left-[40px] md:-left-[80px] md:h-[200px] h-[60px] sm:h-[100px] rotate-[79deg] hidden sm:block" >
                <img src='/images/corner-decorations.png' alt='' className='w-full h-full drop-shadow-lg' />
            </div>
            <div className="absolute bottom-[0] right-[-15px] sm:right-[-40px] md:right-[-80px] md:h-[180px] h-[60px] sm:h-[100px] rotate-[-100deg] hidden sm:block" >
                <img src='/images/corner-decorations.png' alt='' className='w-full h-full drop-shadow-lg' />
            </div>

            {/* Enhanced Title with Gradient Text */}
            <div className="text-center mb-8">
                <p ref={titleRef} className='md:text-5xl text-2xl font-bold my-5 text-[#244473]' >
                    {t('home.who.title')}
                </p>
                
                {/* Decorative Line */}
                <div className="w-60 h-1 bg-gradient-to-r from-transparent via-[#244473] to-transparent mx-auto mt-4 opacity-0 translate-y-8 transition-all duration-700 ease-out delay-300"
                     style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(32px)' }}>
                </div>
            </div>

            {/* Enhanced Content Layout */}
            <div className='w-full max-w-8xl mx-auto px-4 grid lg:grid-cols-2  justify-between items-center '>
                {/* Left Side - Dynamic Description with Interactive Elements */}
                <div className='space-y-6'>
            

                    {/* Main Content Card with Enhanced Styling */}
                    <Box
                        ref={descriptionRef}
                        className='opacity-0 translate-y-8 transition-all duration-700 ease-out delay-200 relative group'
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            background: 'linear-gradient(135deg, rgba(36, 68, 115, 0.05) 0%, rgba(36, 68, 115, 0.1) 100%)',
                            border: '2px solid rgba(36, 68, 115, 0.1)',
                            backdropFilter: 'blur(15px)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(36, 68, 115, 0.1), transparent)',
                                transition: 'left 0.8s ease-in-out',
                            },
                            '&:hover::before': {
                                left: '100%',
                            },
                            '&:hover': {
                                transform: 'translateY(-8px) scale(1.02)',
                                boxShadow: '0 25px 50px rgba(36, 68, 115, 0.2)',
                                background: 'linear-gradient(135deg, rgba(36, 68, 115, 0.1) 0%, rgba(36, 68, 115, 0.18) 100%)',
                                border: '2px solid rgba(36, 68, 115, 0.2)',
                            },
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        {/* Decorative Corner Elements */}
                        <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-[#244473] opacity-20 rounded-tr-lg"></div>
                        <div className="absolute bottom-2 left-2 w-8 h-8 border-b-2 border-l-2 border-[#244473] opacity-20 rounded-bl-lg"></div>
                        
                        {/* Content with Typography Enhancement */}
                        <div className="relative z-10">
                            <div className="flex items-center gap-4 mb-4 ">
                                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#244473] to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 contact-section">
                                    <BusinessCenter className="text-[#244473] text-xl" />
                                </div>
                                <div className="flex-1 ">
                                    <h3 className="text-xl font-bold text-[#244473]  group-hover:text-blue-600 transition-colors duration-300">
                                        {t('home.who.title')}
                                    </h3>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <p className='text-lg md:text-xl text-[#244473] leading-relaxed group-hover:text-gray-700 transition-colors duration-300'>
                                    {t('home.who.description')}
                                </p>
                                
                                {/* Key Highlights */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                                    <div className="flex items-center space-x-2 text-[#244473] opacity-0 animate-fade-in-up" 
                                         style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
                                        <CheckCircle className="text-green-600 text-lg" />
                                        <span className="text-sm font-medium">{t('home.who.highlights.management')}</span>
                                    </div>
                                    <div className="flex items-center space-x-2 text-[#244473] opacity-0 animate-fade-in-up" 
                                         style={{ animationDelay: '0.7s', animationFillMode: 'forwards' }}>
                                        <CheckCircle className="text-green-600 text-lg" />
                                        <span className="text-sm font-medium">{t('home.who.highlights.technology')}</span>
                                    </div>
                                    <div className="flex items-center space-x-2 text-[#244473] opacity-0 animate-fade-in-up" 
                                         style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}>
                                        <CheckCircle className="text-green-600 text-lg" />
                                        <span className="text-sm font-medium">{t('home.who.highlights.growth')}</span>
                                    </div>
                                    <div className="flex items-center space-x-2 text-[#244473] opacity-0 animate-fade-in-up" 
                                         style={{ animationDelay: '1.1s', animationFillMode: 'forwards' }}>
                                        <CheckCircle className="text-green-600 text-lg" />
                                        <span className="text-sm font-medium">{t('home.who.highlights.partnership')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Animated Progress Bar */}
                        <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#244473] to-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
                    </Box>

                    {/* Dynamic Action Cards with Animations */}
                    <div ref={statsRef} className="mt-8 opacity-0 translate-y-8 transition-all duration-700 ease-out delay-400">
                        {/* Main CTA Card */}
                        <div className="relative overflow-hidden rounded-2xl who-cta p-6 mb-6 group hover:shadow-2xl hover:shadow-blue-500/25 transition-all duration-500">
                            
                            <div className="relative z-10 flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-white mb-2 group-hover:scale-105 transition-transform duration-300">
                                        {t('home.who.cta.title')}
                                    </h3>
                                    <p className="text-white/80 text-sm group-hover:text-white transition-colors duration-300">
                                        {t('home.who.cta.description')}
                                    </p>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300 cursor-pointer">
                                    {isRTL ? (
                                        <ArrowBack className="text-white text-xl" href={'/contact'} onClick={() => {
                                            window.location.href = '/contact';
                                        }} />
                                    ) : (
                                        <ArrowForward className="text-white text-xl" href={'/contact'} onClick={() => {
                                            window.location.href = '/contact';
                                        }} />
                                    )}
                                    </div>
                                    
                                </div>
                            </div>
                        </div>

                        {/* Floating Achievement Badges */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="group relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-[#244473] to-[#244473] rounded-xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="relative bg-white rounded-xl p-4 text-center hover:transform hover:-translate-y-2 transition-all duration-300 border-2 border-transparent group-hover:border-[#244473]">
                                    <Star className="text-[#244473] text-2xl mx-auto mb-2 group-hover:rotate-12 transition-transform duration-300" />
                                    <div className="text-sm font-bold text-[#244473] group-hover:text-[#244473] transition-colors duration-300">{t('home.who.badges.excellence')}</div>
                                </div>
                            </div>
                            
                            <div className="group relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-[#244473] to-[#244473] rounded-xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="relative bg-white rounded-xl p-4 text-center hover:transform hover:-translate-y-2 transition-all duration-300 border-2 border-transparent group-hover:border-[#244473]">
                                    <FlashOn className="text-[#244473] text-2xl mx-auto mb-2 group-hover:rotate-12 transition-transform duration-300" />
                                    <div className="text-sm font-bold text-[#244473] group-hover:text-[#244473] transition-colors duration-300">{t('home.who.badges.innovation')}</div>
                                </div>
                            </div>
                            
                            <div className="group relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-[#244473] to-[#244473] rounded-xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="relative bg-white rounded-xl p-4 text-center hover:transform hover:-translate-y-2 transition-all duration-300 border-2 border-transparent group-hover:border-[#244473]">
                                    <Psychology className="text-[#244473] text-2xl mx-auto mb-2 group-hover:rotate-12 transition-transform duration-300" />
                                    <div className="text-sm font-bold text-[#244473] group-hover:text-[#244473] transition-colors duration-300">{t('home.who.badges.strategy')}</div>
                                </div>
                            </div>
                            
                            <div className="group relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-[#244473] to-[#244473] rounded-xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="relative bg-white rounded-xl p-4 text-center hover:transform hover:-translate-y-2 transition-all duration-300 border-2 border-transparent group-hover:border-[#244473]">
                                    <Diamond className="text-[#244473] text-2xl mx-auto mb-2 group-hover:rotate-12 transition-transform duration-300" />
                                    <div className="text-sm font-bold text-[#244473] group-hover:text-[#244473] transition-colors duration-300">{t('home.who.badges.premium')}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Enhanced DisplayCards */}
                <div className='relative '>
                    <div className='absolute rtl:left-0 ltr:left-10 bottom-0'>
                        <DisplayCards />
                    </div>
                </div>
            </div>
        </section>
    )
}